import React, { useState, useEffect, useMemo, useContext } from 'react';
import MaterialTable from "material-table";
import { useSelector } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { FirebaseContext } from 'common';
import { useTranslation } from "react-i18next";
import { colors } from '../components/Theme/WebTheme';
import Switch from "@mui/material/Switch";
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import { useUsersPage } from '../common/useUsersPage';
import UsersPageFooter from '../components/UsersPageFooter';
import UsersCombo from '../components/UsersCombo';
import { Select, MenuItem, FormControl, InputLabel, Box } from '@mui/material';
import { subscribeWorkshops, saveWorkshop, deleteWorkshop, businessTypeLabel } from '../common/workshopService';

export default function Workshops() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const auth = useSelector(state => state.auth);
  const { database } = useContext(FirebaseContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isFleetAdmin = auth.profile?.usertype === 'fleetadmin';

  const [usertype, setUsertype] = useState('driver');
  const [selectedUser, setSelectedUser] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [loadingWs, setLoadingWs] = useState(false);

  const {
    items,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
  } = useUsersPage(isFleetAdmin ? 'driver' : usertype, {
    fleetadminUid: isFleetAdmin ? auth.profile?.uid : null,
    enabled: !!auth.profile?.uid,
  });

  const userOptions = useMemo(() => (items || []).map((u) => ({
    desc: `${u.firstName || ''} ${u.lastName || ''}`.trim() + (u.mobile ? ` (${u.mobile})` : (u.email ? ` (${u.email})` : '')),
    uid: u.id,
  })).filter((o) => o.uid), [items]);

  // Al volver desde edición, reseleccionar el usuario.
  useEffect(() => {
    if (location.state?.userId && userOptions.length && !selectedUser) {
      const match = userOptions.find((o) => o.uid === location.state.userId);
      if (match) setSelectedUser(match);
    }
  }, [location.state, userOptions, selectedUser]);

  useEffect(() => {
    if (!selectedUser?.uid) {
      setWorkshops([]);
      return;
    }
    setLoadingWs(true);
    const unsub = subscribeWorkshops(database, selectedUser.uid, (list) => {
      setWorkshops(list);
      setLoadingWs(false);
    });
    return unsub;
  }, [database, selectedUser]);

  const toggleActive = async (rowData) => {
    if (!settings.AllowCriticalEditsAdmin) {
      alert(t('demo_mode'));
      return;
    }
    try {
      await saveWorkshop(database, selectedUser.uid, { ...rowData, active: !rowData.active }, rowData.id);
    } catch (e) {
      alert(t('update_failed', { defaultValue: 'No se pudo actualizar' }));
    }
  };

  const columns = [
    { title: t('workshop_name', { defaultValue: 'Taller' }), field: 'name' },
    {
      title: t('workshop_type', { defaultValue: 'Tipo' }), field: 'businessType',
      render: (rowData) => businessTypeLabel(rowData.businessType, t),
    },
    { title: t('mobile'), field: 'phone' },
    { title: t('workshop_address', { defaultValue: 'Dirección' }), field: 'address' },
    {
      title: t('workshop_active', { defaultValue: 'Activo' }), field: 'active', type: 'boolean',
      render: (rowData) => (
        <Switch
          disabled={!settings.AllowCriticalEditsAdmin}
          checked={!!rowData.active}
          onChange={() => toggleActive(rowData)}
        />
      ),
    },
  ];

  const [selectedRow, setSelectedRow] = useState(null);

  if (loading && !items.length) return <CircularLoading />;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {!isFleetAdmin ? (
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>{t('usertype', { defaultValue: 'Tipo de usuario' })}</InputLabel>
            <Select
              value={usertype}
              label={t('usertype', { defaultValue: 'Tipo de usuario' })}
              onChange={(e) => { setUsertype(e.target.value); setSelectedUser(null); }}
            >
              <MenuItem value="driver">{t('driver', { defaultValue: 'Socio conductor' })}</MenuItem>
              <MenuItem value="customer">{t('customer', { defaultValue: 'Cliente' })}</MenuItem>
            </Select>
          </FormControl>
        ) : null}
        <Box sx={{ minWidth: 280, flex: 1 }}>
          <UsersCombo
            users={userOptions}
            value={selectedUser}
            placeholder={t('workshop_select_user', { defaultValue: 'Selecciona un usuario' })}
            onChange={(e, v) => setSelectedUser(v)}
            style={{ width: '100%' }}
          />
        </Box>
      </Box>
      {!selectedUser ? (
        <Box sx={{ p: 3, textAlign: 'center', color: colors.SHADOW }}>
          {t('workshop_select_user_hint', { defaultValue: 'Selecciona un usuario para ver y gestionar sus talleres.' })}
        </Box>
      ) : loadingWs ? <CircularLoading /> : (
        <MaterialTable
          title={t('workshops_title', { defaultValue: 'Talleres' }) + ' — ' + selectedUser.desc}
          columns={columns}
          style={{
            direction: isRTL === "rtl" ? "rtl" : "ltr",
            borderRadius: "8px",
            boxShadow: '0px 2px 5px rgba(0,0,0,0.15)',
            padding: "20px",
          }}
          data={workshops}
          onRowClick={((evt, row) => setSelectedRow(row.tableData.id))}
          options={{
            rowStyle: rowData => ({
              backgroundColor: (selectedRow === rowData.tableData.id) ? colors.ROW_SELECTED : colors.WHITE
            }),
            ...TableStyle(settings)
          }}
          localization={localization(t)}
          editable={{
            onRowDelete: (oldData) => settings.AllowCriticalEditsAdmin ?
              new Promise((resolve, reject) => {
                deleteWorkshop(database, selectedUser.uid, oldData.id).then(resolve).catch(reject);
              })
              :
              new Promise((resolve) => {
                setTimeout(() => {
                  resolve();
                  alert(t('demo_mode'));
                }, 600);
              })
          }}
          actions={[
            {
              icon: 'add',
              tooltip: t('workshop_add', { defaultValue: 'Agregar taller' }),
              isFreeAction: true,
              onClick: () => navigate(`/workshops/edit/${selectedUser.uid}`),
            },
            {
              icon: 'edit',
              tooltip: t('edit', { defaultValue: 'Editar' }),
              onClick: (event, rowData) => {
                navigate(`/workshops/edit/${selectedUser.uid}/${rowData.id}`);
              },
            },
          ]}
        />
      )}
      <UsersPageFooter
        count={userOptions.length}
        hasMore={hasMore}
        loading={loading}
        error={error}
        onLoadMore={loadMore}
        onRetry={refresh}
      />
    </ThemeProvider>
  );
}
